function readFirst(source, keys, fallback = null) {
   if (!source || typeof source !== "object") return fallback;

   for (const key of keys) {
      const value = source?.[key];
      if (value !== undefined && value !== null && value !== "") return value;
   }

   return fallback;
}

function readStart(item) {
   return (
      item?.startTime ??
      item?.start_time ??
      item?.start ??
      item?.dateTime ??
      item?.datetime ??
      item?.date ??
      item?.reservation?.startTime ??
      item?.reservation?.start_time ??
      item?.reservation?.start ??
      item?.reservation?.dateTime ??
      item?.reservation?.datetime ??
      item?.reservation?.date ??
      null
   );
}

function readReservationId(item) {
   return readFirst(
      item,
      ["id", "_id", "reservationId", "reservation_id", "reservationID"],
      readFirst(item?.reservation, [
         "id",
         "_id",
         "reservationId",
         "reservation_id",
         "reservationID",
      ]),
   );
}

function normalizeStatus(status, fallback = "") {
   return String(status || fallback)
      .trim()
      .toUpperCase();
}

function readRegularStatus(item) {
   return normalizeStatus(
      readFirst(
         item,
         [
            "status",
            "state",
            "reservationStatus",
            "reservation_status",
            "isCancelled",
            "isCanceled",
            "is_cancelled",
            "is_canceled",
         ],
         readFirst(item?.reservation, [
            "status",
            "state",
            "reservationStatus",
            "reservation_status",
            "isCancelled",
            "isCanceled",
            "is_cancelled",
            "is_canceled",
         ]),
      ),
   );
}

function isCancelledRegular(item) {
   const status = readRegularStatus(item);
   return (
      status === "CANCELLED" ||
      status === "CANCELED" ||
      status === "ANULAT" ||
      item?.isCancelled === true ||
      item?.isCanceled === true ||
      item?.is_cancelled === true ||
      item?.is_canceled === true ||
      item?.canceledAt ||
      item?.cancelledAt ||
      item?.reservation?.isCancelled === true ||
      item?.reservation?.isCanceled === true ||
      item?.reservation?.is_cancelled === true ||
      item?.reservation?.is_canceled === true ||
      item?.reservation?.canceledAt ||
      item?.reservation?.cancelledAt
   );
}

function readRawDateTimeParts(val) {
   const text = String(val || "").trim();
   if (!text) return null;

   const match = text.match(
      /(\d{4})-(\d{2})-(\d{2})(?:[T\s]+)(\d{2}):(\d{2})/,
   );
   if (!match) return null;

   const [, year, month, day, hour, minute] = match;
   return { year, month, day, hour, minute };
}

export function fmtReservationDate(val) {
   if (!val) return "—";
   const raw = readRawDateTimeParts(val);
   if (raw) return `${raw.day}.${raw.month}.${raw.year}`;

   const d = new Date(val);
   if (Number.isNaN(d.getTime())) return String(val);
   return d.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
   });
}

export function fmtReservationTime(val) {
   if (!val) return "—";
   const raw = readRawDateTimeParts(val);
   if (raw) return `${raw.hour}:${raw.minute}`;

   const d = new Date(val);
   if (Number.isNaN(d.getTime())) return "—";
   return d.toLocaleTimeString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
   });
}

function readSector(item) {
   const raw = item?.sector ?? item?.reservation?.sector ?? "";
   const value = String(raw || "").trim();
   return value || "—";
}

function readGearbox(item) {
   const raw = item?.gearbox ?? item?.reservation?.gearbox ?? "";
   const value = String(raw || "").trim();
   return value || "—";
}

function toMs(val) {
   if (!val) return 0;
   const d = new Date(val);
   const ms = d.getTime();
   return Number.isFinite(ms) ? ms : 0;
}

export function buildLessonCardItem(item, index, options = {}) {
   const id = readReservationId(item);
   const start = readStart(item);
   const cancelled = isCancelledRegular(item);
   const sortMs = toMs(start);
   const isFuture = sortMs > Date.now();

   return {
      kind: "lesson",
      typeLabel: "Lecție",
      id,
      key: id != null ? `lesson-${String(id)}` : `lesson-${String(start || "x")}-${index}`,
      date: fmtReservationDate(start),
      time: fmtReservationTime(start),
      extra: `${readSector(item)} · ${readGearbox(item)}`,
      sector: readSector(item),
      gearbox: readGearbox(item),
      sortMs,
      inactive: cancelled,
      statusLabel: cancelled ? "Anulat" : isFuture ? "Programat" : "",
      current: Boolean(options.current) || isFuture,
      canCancel: false,
      raw: item,
   };
}

const PRACTICAL_FINAL_STATUSES = new Set([
   "PASSED",
   "PROMOTED",
   "PROMOVAT",
   "ADMIS",
   "FAILED",
   "RESPINS",
   "ABSENT",
   "CANCELLED",
   "CANCELED",
   "ANULAT",
]);

const PRACTICAL_STATUS_LABELS = {
   SCHEDULED: "Programat",
   PASSED: "Admis",
   PROMOTED: "Admis",
   PROMOVAT: "Admis",
   ADMIS: "Admis",
   FAILED: "Respins",
   RESPINS: "Respins",
   ABSENT: "Absent",
   CANCELLED: "Anulat",
   CANCELED: "Anulat",
   ANULAT: "Anulat",
};

function readPracticalSlot(reservation) {
   return (
      reservation?.slot ??
      reservation?.practicalExamSlot ??
      reservation?.examSlot ??
      reservation?.PracticalExamSlot ??
      reservation?.practical_exam_slot ??
      null
   );
}

function readPracticalDate(reservation) {
   const slot = readPracticalSlot(reservation);
   const date = readFirst(
      reservation,
      [
         "dateKey",
         "date",
         "slotDate",
         "slot_date",
         "examDate",
         "exam_date",
         "startAt",
         "start_at",
         "startsAt",
         "starts_at",
      ],
      readFirst(slot, [
         "dateKey",
         "date",
         "slotDate",
         "slot_date",
         "examDate",
         "exam_date",
         "startAt",
         "start_at",
         "startsAt",
         "starts_at",
      ]),
   );

   return typeof date === "string" && date.includes("T")
      ? date.slice(0, 10)
      : date
        ? String(date).slice(0, 10)
        : "";
}

function readPracticalTime(reservation) {
   const slot = readPracticalSlot(reservation);
   const time = readFirst(
      reservation,
      [
         "timeKey",
         "time",
         "slotTime",
         "slot_time",
         "examTime",
         "exam_time",
         "startTime",
         "start_time",
         "startAt",
         "start_at",
         "startsAt",
         "starts_at",
      ],
      readFirst(slot, [
         "timeKey",
         "time",
         "slotTime",
         "slot_time",
         "examTime",
         "exam_time",
         "startTime",
         "start_time",
         "startAt",
         "start_at",
         "startsAt",
         "starts_at",
      ]),
   );

   if (typeof time === "string") {
      const isoMatch = time.match(/[T\s](\d{2}):(\d{2})/);
      if (isoMatch) return `${isoMatch[1]}:${isoMatch[2]}`;
      const timeMatch = time.match(/^(\d{1,2}):(\d{2})/);
      if (timeMatch) {
         return `${String(Number(timeMatch[1])).padStart(2, "0")}:${timeMatch[2]}`;
      }
   }

   return time ? String(time) : "";
}

function readPracticalStatus(reservation) {
   return normalizeStatus(
      readFirst(
         reservation,
         ["status", "result", "examStatus", "exam_status", "examResult", "verdict"],
         "SCHEDULED",
      ),
      "SCHEDULED",
   );
}

function readPracticalSector(reservation) {
   const slot = readPracticalSlot(reservation);
   return String(readFirst(reservation, ["sector"], readFirst(slot, ["sector"], "—")));
}

function readPracticalGearbox(reservation) {
   const slot = readPracticalSlot(reservation);
   return String(
      readFirst(reservation, ["gearbox", "cutie"], readFirst(slot, ["gearbox"], "—")),
   );
}

export function readPracticalStartValue(reservation) {
   const date = readPracticalDate(reservation);
   const time = readPracticalTime(reservation) || "00:00";
   return date ? `${date}T${time}:00` : "";
}

export function buildPracticalCardItem(reservation, index, options = {}) {
   if (!reservation) return null;

   const id = readReservationId(reservation);
   const status = readPracticalStatus(reservation);
   const finalStatus = PRACTICAL_FINAL_STATUSES.has(status);
   const start = readPracticalStartValue(reservation);
   const statusLabel = PRACTICAL_STATUS_LABELS[status] || status || "Programat";

   return {
      kind: "practicalExam",
      typeLabel: "Examen",
      id,
      key:
         id != null
            ? `practical-${String(id)}`
            : `practical-${String(start || "x")}-${index}`,
      date: fmtReservationDate(start),
      time: fmtReservationTime(start),
      extra: `${readPracticalSector(reservation)} · ${readPracticalGearbox(reservation)}`,
      sector: readPracticalSector(reservation),
      gearbox: readPracticalGearbox(reservation),
      sortMs: toMs(start),
      inactive: finalStatus,
      statusLabel,
      status,
      current: Boolean(options.current),
      canCancel: false,
      raw: reservation,
   };
}
